import { useEffect, useState, useCallback } from "react";
import { supplierDeliveryAPI } from "../services/supplierApi";
import { Truck, Loader2, ChevronLeft, Info, ArrowRight, Package } from "lucide-react";
import Swal from "../../utils/apparifySwal";
import { StatusBadge } from "../../components/erp";
import { getDeliveryLabel, getPOLabel } from "../../utils/documentLabels";

const TRANSITIONS = {
  DRAFT: { next: "PREPARED", label: "Mark Prepared", requiresTracking: false },
  PREPARED: { next: "DISPATCHED", label: "Dispatch Delivery", requiresTracking: false },
  DISPATCHED: { next: "IN_TRANSIT", label: "Mark In Transit", requiresTracking: false },
  IN_TRANSIT: { next: "DELIVERED", label: "Mark Delivered", requiresTracking: false },
};

const FLOW = ["DRAFT", "PREPARED", "DISPATCHED", "IN_TRANSIT", "DELIVERED"];

export default function SupplierDeliveries() {
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await supplierDeliveryAPI.list({ limit: 200 });
      setDeliveries(res?.data || []);
    } catch {
      setDeliveries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const openDetail = useCallback(async (delivery) => {
    setDetailLoading(true);
    try {
      const res = await supplierDeliveryAPI.getById(delivery.id);
      setSelected(res?.data || delivery);
    } catch {
      setSelected(delivery);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleTransition = async (delivery) => {
    const tr = TRANSITIONS[delivery.status];
    if (!tr) return;

    const confirm = await Swal.fire({
      title: tr.label,
      text: "Update this delivery status?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: tr.label,
      confirmButtonColor: "#2563eb",
    });
    if (!confirm.isConfirmed) return;

    setUpdating(true);
    try {
      const res = await supplierDeliveryAPI.update(delivery.id, { status: tr.next });
      setSelected(res?.data || null);
      await fetchList();
      Swal.fire({ icon: "success", title: "Updated", timer: 1400, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Failed", text: err?.message || "Could not update delivery" });
    } finally {
      setUpdating(false);
    }
  };

  if (selected) {
    const tr = TRANSITIONS[selected.status];
    return (
      <div className="operations-page max-w-5xl">
        <button onClick={() => setSelected(null)} className="flex items-center gap-1 text-xs text-blue-600 hover:underline dark:text-blue-400">
          <ChevronLeft className="h-3 w-3" /> Back to Deliveries
        </button>

        {detailLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
        ) : (
          <>
            <div className="operations-surface p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <h2 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">{getDeliveryLabel(selected)}</h2>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                    {getPOLabel(selected)} | {selected.business_name || "Business"}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2 justify-end">
                  <StatusBadge status={selected.status} context="supplier" />
                  <StatusBadge status={selected.receiving_status || "PENDING_RECEIPT"} />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div>
                  <p className="text-neutral-400 mb-0.5">Carrier</p>
                  <p className="font-medium text-neutral-700 dark:text-neutral-200">{selected.carrier || "Not set"}</p>
                </div>
                <div>
                  <p className="text-neutral-400 mb-0.5">Tracking #</p>
                  <p className="font-medium text-neutral-700 dark:text-neutral-200 font-mono">{selected.tracking_number || "Not set"}</p>
                </div>
                <div>
                  <p className="text-neutral-400 mb-0.5">Expected Arrival</p>
                  <p className="font-medium text-neutral-700 dark:text-neutral-200">{selected.estimated_arrival_at ? new Date(selected.estimated_arrival_at).toLocaleDateString() : "-"}</p>
                </div>
                <div>
                  <p className="text-neutral-400 mb-0.5">Business Receipt</p>
                  <p className="font-medium text-neutral-700 dark:text-neutral-200">{String(selected.receiving_status || "PENDING_RECEIPT").replace(/_/g, " ")}</p>
                </div>
              </div>

              {selected.notes && (
                <div className="mt-4 rounded-xl bg-neutral-50 dark:bg-neutral-800/50 p-3 text-xs text-neutral-600 dark:text-neutral-300">
                  {selected.notes}
                </div>
              )}
            </div>

            <div className="operations-surface p-5">
              <h3 className="text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-3">Supplier Delivery Progress</h3>
              <div className="flex items-center gap-2 text-xs overflow-x-auto pb-1">
                {FLOW.map((step, index) => {
                  const activeIndex = FLOW.indexOf(selected.status);
                  const passed = activeIndex >= index;
                  const isCurrent = selected.status === step;
                  return (
                    <div key={step} className="flex items-center gap-2">
                      <div className={`flex h-7 w-7 items-center justify-center rounded-full text-[10px] font-bold ${
                        isCurrent
                          ? "bg-blue-600 text-white"
                          : passed
                            ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                            : "bg-neutral-100 text-neutral-400 dark:bg-neutral-800 dark:text-neutral-500"
                      }`}>
                        {index + 1}
                      </div>
                      <span className={`hidden sm:inline ${isCurrent ? "font-semibold text-neutral-900 dark:text-neutral-50" : passed ? "text-emerald-600 dark:text-emerald-400" : "text-neutral-400"}`}>
                        {step.replace(/_/g, " ")}
                      </span>
                      {index < FLOW.length - 1 && <ArrowRight className="h-3 w-3 text-neutral-300 dark:text-neutral-600" />}
                    </div>
                  );
                })}
              </div>
            </div>

            {Array.isArray(selected.lines) && selected.lines.length > 0 && (
              <div className="operations-surface operations-table-wrap p-5">
                <h3 className="text-xs font-medium text-neutral-700 dark:text-neutral-200 mb-3">Shipment Lines</h3>
                <table className="w-full text-xs">
                  <thead className="text-neutral-500 dark:text-neutral-400 border-b border-neutral-200 dark:border-neutral-700">
                    <tr>
                      <th className="pb-2 pr-3 text-left font-medium">Variant</th>
                      <th className="pb-2 pr-3 text-right font-medium">Shipped</th>
                      <th className="pb-2 pr-3 text-right font-medium">Received</th>
                      <th className="pb-2 pr-3 text-right font-medium">Accepted</th>
                      <th className="pb-2 text-right font-medium">Rejected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.lines.map((line) => (
                      <tr key={line.id} className="border-b border-neutral-100 dark:border-neutral-800 last:border-0">
                        <td className="py-2 pr-3 text-neutral-800 dark:text-neutral-100">{line.variantTitle || line.variant_title || line.variantSku || "Requested item"}</td>
                        <td className="py-2 pr-3 text-right text-neutral-700 dark:text-neutral-200">{line.expectedQuantity || line.expected_quantity || 0}</td>
                        <td className="py-2 pr-3 text-right text-neutral-600 dark:text-neutral-300">{line.receivedQuantity || line.received_quantity || 0}</td>
                        <td className="py-2 pr-3 text-right text-neutral-600 dark:text-neutral-300">{line.acceptedQuantity || line.accepted_quantity || 0}</td>
                        <td className="py-2 text-right text-neutral-600 dark:text-neutral-300">{line.rejectedQuantity || line.rejected_quantity || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tr && (
              <div className="rounded-2xl border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950/20 p-5">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-1">Next Supplier Action</h3>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                      {selected.tracking_number
                        ? `Move this delivery to ${tr.next.replace(/_/g, " ")}.`
                        : `Move this delivery to ${tr.next.replace(/_/g, " ")}. The tracking reference was generated when the delivery was created.`}
                    </p>
                    <button
                      onClick={() => handleTransition(selected)}
                      disabled={updating}
                      className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition disabled:opacity-50"
                    >
                      {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Truck className="h-4 w-4" />}
                      {updating ? "Updating..." : tr.label}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {selected.status === "DELIVERED" && (
              <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/20 p-4 text-xs text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                <Package className="h-4 w-4" />
                The shipment is marked delivered. Stock will only move after the business records accepted receipt.
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div className="operations-page">
      <div className="operations-page-header">
        <div>
          <h1 className="text-base font-semibold text-neutral-900 dark:text-neutral-50">Deliveries</h1>
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Track supplier shipment progress and business receipt status separately.</p>
        </div>
        <span className="text-xs text-neutral-400">{deliveries.length} total</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div>
      ) : deliveries.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-neutral-200 dark:border-neutral-700 p-8 text-center">
          <Truck className="mx-auto h-8 w-8 text-neutral-300 dark:text-neutral-600 mb-2" />
          <p className="text-sm font-medium text-neutral-500 dark:text-neutral-400">No deliveries yet</p>
          <p className="text-xs text-neutral-400 dark:text-neutral-500 mt-1">Create draft deliveries from the Purchase Orders page. The PO will be acknowledged automatically when the delivery is created.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {deliveries.map((delivery) => (
            <button
              key={delivery.id}
              onClick={() => openDetail(delivery)}
              className="operations-surface w-full p-4 text-left transition hover:border-neutral-300 dark:hover:border-neutral-600"
            >
              <div className="flex items-center justify-between gap-3 mb-2">
                <div>
                  <span className="text-sm font-medium text-neutral-900 dark:text-neutral-50">{getDeliveryLabel(delivery)}</span>
                  <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                    {getPOLabel(delivery)} | {delivery.business_name || "Business"}
                  </p>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <StatusBadge status={delivery.status} context="supplier" />
                  <StatusBadge status={delivery.receiving_status || "PENDING_RECEIPT"} />
                </div>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-500 dark:text-neutral-400">
                {delivery.tracking_number && <span className="font-mono">{delivery.tracking_number}</span>}
                {delivery.carrier && <span>{delivery.carrier}</span>}
                {delivery.estimated_arrival_at && <span>ETA {new Date(delivery.estimated_arrival_at).toLocaleDateString()}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
